import { getConfig, isTest } from 'pkg/config/app';

import { delay } from 'pkg/timings';
import { UrlParser } from 'pkg/url';
import uuid from 'pkg/uuid';

import * as logger from 'core/logger';

const urlParser = new UrlParser();

// Reads within this window after a write go to the primary database node, to
// compensate for replication latency.
const ReadPrimaryWindowMs = 3000;

let token = '';
let easyAuthToken = '';
let lastWriteTimestamp = 0;

export function lastWriteAt(): number {
	return lastWriteTimestamp;
}

interface Headers {
	[key: string]: string;
}

const defaultRequestHeaders: Headers = {
	'Content-Type': 'application/json',
	'X-Correlation-Id': uuid(),
};

export function setToken(t: string): void {
	token = t;
}

export function setEasyAuthToken(t: string): void {
	easyAuthToken = t;
}

export function hasToken(): boolean {
	return token?.length > 0;
}

export function setDefaultRequestHeader(header: string, value: string): void {
	if (!value) {
		delete defaultRequestHeaders[header];
		return;
	}

	defaultRequestHeaders[header] = value;
}

// With the dev EasyAuth proxy active, the Entra bearer takes Authorization (for
// the gate) and the account token moves to X-TS-Authorization (read by the API).
function buildAuthHeaders(): Headers {
	if (EASYAUTH_DEV_BUILD && easyAuthToken.length > 0) {
		if (token.length === 0) {
			return { Authorization: `Bearer ${easyAuthToken}` };
		}

		return {
			Authorization: `Bearer ${easyAuthToken}`,
			'X-TS-Authorization': `Bearer ${token}`,
		};
	}

	if (token.length === 0) {
		return {};
	}

	return { Authorization: `Bearer ${token}` };
}

function buildBody(payload: unknown, headers: Headers): BodyInit {
	if (payload instanceof FormData) {
		return payload;
	}

	if (headers['Content-Type'] === 'application/json') {
		return JSON.stringify(payload);
	}

	const params = new URLSearchParams();

	Object.entries(payload).forEach(([key, value]) => {
		params.append(key, `${value}`);
	});

	return params;
}

function buildUrl(requestUrl: string): string {
	const parts = [getConfig().apiUrl];

	if (!requestUrl.startsWith('/v1/')) {
		parts.push('v1');
	}

	parts.push(requestUrl);

	return parts.join('/').replace(/([^:])(\/\/+)/g, '$1/');
}

export async function request(
	requestUrl = '',
	method = 'GET',
	payload = {},
	additionalHeaders = {},
	retryable = false
): Promise<Response> {
	const headers: Headers = {
		...defaultRequestHeaders,
		...buildAuthHeaders(),
		...additionalHeaders,
		'X-Client-Version': getConfig().version,
		'X-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
	};

	const requestOptions: RequestInit = {
		credentials: 'include',
		mode: 'cors',
		method,
		headers,
	};

	if (method !== 'GET' && method !== 'HEAD') {
		requestOptions.body = buildBody(payload, headers);
	}

	// Lets the browser set the multipart content type and boundary itself.
	if (
		payload instanceof FormData ||
		headers['Content-Type'] === 'multipart/form-data'
	) {
		delete headers['Content-Type'];
	}

	const url = buildUrl(requestUrl);
	const maxRetries = isTest() ? 0 : 10;

	let retries = 0;
	let easyAuthRetried = false;

	const makeRequest = async (): Promise<Response> => {
		let response: Response;

		try {
			response = await fetch(url, requestOptions);
		} catch (e) {
			if (!retryable) {
				throw e;
			}

			if (retries >= maxRetries) {
				throw new Error('failed after max retries', { cause: e });
			}

			retries++;

			logger.debug('retrying request to', { url, delay: 50 * retries });

			await delay(50 * retries);

			return await makeRequest();
		}

		// The local proxy tags EasyAuth's login challenge with X-EasyAuth-Login,
		// unlike a genuine API 401. Refresh silently and retry in place; only an
		// expired Entra session forces the interactive redirect.
		if (
			EASYAUTH_DEV_BUILD &&
			response.status === 401 &&
			!easyAuthRetried &&
			response.headers.get('X-EasyAuth-Login')
		) {
			const easyAuthDev = await import('pkg/auth/easyauth_dev');

			if (easyAuthDev.isEasyAuthDevEnabled()) {
				easyAuthRetried = true;

				if (await easyAuthDev.reauthSilent()) {
					requestOptions.headers = { ...headers, ...buildAuthHeaders() };

					return await makeRequest();
				}

				await easyAuthDev.login();
			}
		}

		return response;
	};

	return await makeRequest();
}

async function write(
	method: string,
	retryable: boolean,
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
): Promise<Response> {
	const result = await request(
		urlParser.transform(uriPattern, uriParams),
		method,
		requestPayload,
		additionalHeaders,
		retryable
	);

	lastWriteTimestamp = Date.now();

	return result;
}

export async function get(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
): Promise<Response> {
	if (Date.now() - lastWriteTimestamp < ReadPrimaryWindowMs) {
		additionalHeaders = { ...additionalHeaders, 'X-Read-Primary': 'true' };
	}

	return await request(
		urlParser.transform(uriPattern, uriParams),
		'GET',
		requestPayload,
		additionalHeaders,
		true
	);
}

export async function post(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
): Promise<Response> {
	return await write(
		'POST',
		false,
		uriPattern,
		uriParams,
		requestPayload,
		additionalHeaders
	);
}

export async function postWithRetry(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
): Promise<Response> {
	return await write(
		'POST',
		true,
		uriPattern,
		uriParams,
		requestPayload,
		additionalHeaders
	);
}

export async function put(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
): Promise<Response> {
	return await write(
		'PUT',
		false,
		uriPattern,
		uriParams,
		requestPayload,
		additionalHeaders
	);
}

export async function putWithRetry(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
): Promise<Response> {
	return await write(
		'PUT',
		true,
		uriPattern,
		uriParams,
		requestPayload,
		additionalHeaders
	);
}

export async function patch(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
): Promise<Response> {
	return await write(
		'PATCH',
		false,
		uriPattern,
		uriParams,
		requestPayload,
		additionalHeaders
	);
}

export async function destroy(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
): Promise<Response> {
	return await write(
		'DELETE',
		false,
		uriPattern,
		uriParams,
		requestPayload,
		additionalHeaders
	);
}
